import useSubscription from '@src/hooks/useSubscription';
import HorizontalLine from '@src/sld/components/Line/HorizontalLine';
import VerticalLine from '@src/sld/components/Line/VerticalLine';
import Unit from '@src/sld/components/Unit/Unit';
import { UnitConfig } from '@src/sld/types';
import { getUnitConfig } from '@src/sld/utils';
import { DataPointWithValue } from '@src/types/DataPoint';
import { ReactElement, useMemo } from 'react';
import { useUnitContext } from './UnitContext';

function Switch1Units(): ReactElement {
  const { unitWidgetConfig, getDataPointValue } = useUnitContext();

  // Get the devices this component will display (first 6 units)
  const displayedDevices = useMemo(() => {
    return [...(unitWidgetConfig?.devices || [])]
      .sort((a, b) => {
        const getDeviceNumber = (deviceId: string) => {
          const match = deviceId.match(/(\d+)$/);
          return match ? parseInt(match[1], 10) : 0;
        };
        return getDeviceNumber(a.deviceId) - getDeviceNumber(b.deviceId);
      })
      .slice(0, 6);
  }, [unitWidgetConfig]);

  // Only subscribe to data points for the devices this component displays
  const dataPointsToSubscribe = useMemo(() => {
    const deviceEngineeringIds = displayedDevices.map((d) => d.engineeringId);
    return (
      unitWidgetConfig?.dataPoints?.filter((dp) =>
        deviceEngineeringIds.includes(dp.controllerId),
      ) || []
    );
  }, [unitWidgetConfig, displayedDevices]);

  const dataPointsWithValue: DataPointWithValue[] = useSubscription(
    dataPointsToSubscribe,
  );

  const units = useMemo(() => {
    return displayedDevices.map((device) => ({
      unitLabel: device.name,
      kw: getDataPointValue(device.engineeringId, 'kW', dataPointsWithValue),
      kvar: getDataPointValue(
        device.engineeringId,
        'kVAR',
        dataPointsWithValue,
      ),
      soc: getDataPointValue(device.engineeringId, 'SOC', dataPointsWithValue),
      racksonline: getDataPointValue(
        device.engineeringId,
        'racks_online',
        dataPointsWithValue,
      ),
      invstatus: getDataPointValue(
        device.engineeringId,
        'InvStatus',
        dataPointsWithValue,
      ),
      runmode: getDataPointValue(
        device.engineeringId,
        'RunMode',
        dataPointsWithValue,
      ),
    })) as UnitConfig[];
  }, [displayedDevices, dataPointsWithValue, getDataPointValue]);

  if (!units || units.length === 0) {
    return <></>;
  }

  return (
    <g transform='translate(-50,-400)'>
      {/* top vertical line */}
      <VerticalLine
        length={41}
        color='white'
        width={0.5}
        translateXCoordinate={430}
        translateYCoordinate={715}
      />

      {/* top horizontal line */}
      <HorizontalLine
        length={400}
        color='white'
        width={0.5}
        translateXCoordinate={225}
        translateYCoordinate={756}
      />

      {/* units */}
      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={225}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[0])}
        rectangleXCoordinate='190'
        rectangleYCoordinate='782'
        textStartingXCoordinate={195}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={125}
        lineTransformYCoordinate={757}
        skipHorizontalLine
      />

      <VerticalLine
        length={75}
        color='white'
        width={0.5}
        translateXCoordinate={300}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[1])}
        rectangleXCoordinate='270'
        rectangleYCoordinate='782'
        textStartingXCoordinate={275}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={215}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={390}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[2])}
        rectangleXCoordinate='350'
        rectangleYCoordinate='782'
        textStartingXCoordinate={355}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={280}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={470}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[3])}
        rectangleXCoordinate='430'
        rectangleYCoordinate='782'
        textStartingXCoordinate={435}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={345}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={550}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[4])}
        rectangleXCoordinate='510'
        rectangleYCoordinate='782'
        textStartingXCoordinate={515}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={410}
        lineTransformYCoordinate={757}
      />

      <VerticalLine
        length={25}
        color='white'
        width={0.5}
        translateXCoordinate={625}
        translateYCoordinate={756}
      />
      <Unit
        unitConfig={getUnitConfig(units[5])}
        rectangleXCoordinate='590'
        rectangleYCoordinate='782'
        textStartingXCoordinate={595}
        textStartingYCoordinate={780}
        lineTransformXCoordinate={475}
        lineTransformYCoordinate={757}
      />
    </g>
  );
}

export default Switch1Units;
